/**
 * Recipe Builder Hook
 *
 * State management for the recipe builder with local editing and dirty tracking.
 */

import { useState, useCallback, useMemo } from 'react';
import type {
  RecipeWithRelations,
  UpdateRecipeDto,
  RecipeIngredientDto,
} from '@bstock/shared';
import type { RawProductWithRelations } from '@bstock/shared';

// ========================================
// TYPES
// ========================================

export interface IngredientState {
  id: string;
  rawProductId: string;
  rawProduct: {
    id: string;
    sku: string;
    name: string;
    isActive: boolean;
    recipeUnit: {
      id: string;
      code: string;
      name: string;
      symbol: string;
    };
    allergens?: string[];
  };
  quantity: number;
  notes: string | null;
  sortOrder: number;
  isNew: boolean;
  isModified: boolean;
  isDeleted: boolean;
  /** Cost in cents (quantity * PMP), null if PMP missing */
  cost: number | null;
}

export interface RecipeBuilderState {
  recipe: RecipeWithRelations | null;
  name: string;
  description: string | null;
  yield: number | null;
  yieldUnitId: string | null;
  posProductIds: string[];
  ingredients: IngredientState[];
  isDirty: boolean;
  errors: Record<string, string>;
}

export interface SelectedPosProduct {
  id: string;
  name: string;
  posCategory: string | null;
}

export interface RecipeBuilderActions {
  setRecipe: (recipe: RecipeWithRelations) => void;
  updateRecipeInfo: (field: 'name' | 'description' | 'yield' | 'yieldUnitId', value: unknown) => void;
  setPosProductIds: (ids: string[]) => void;
  addPosProduct: (id: string) => void;
  removePosProduct: (id: string) => void;
  addIngredient: (rawProduct: RawProductWithRelations) => void;
  updateIngredient: (id: string, field: 'quantity' | 'notes', value: unknown) => void;
  removeIngredient: (id: string) => void;
  reorderIngredients: (ids: string[]) => void;
  getUpdateDto: () => UpdateRecipeDto;
  reset: () => void;
  hasErrors: () => boolean;
}

// ========================================
// HOOK
// ========================================

export function useRecipeBuilder() {
  const [state, setState] = useState<RecipeBuilderState>({
    recipe: null,
    name: '',
    description: null,
    yield: null,
    yieldUnitId: null,
    posProductIds: [],
    ingredients: [],
    isDirty: false,
    errors: {},
  });

  // Set recipe from server data
  const setRecipe = useCallback((recipe: RecipeWithRelations) => {
    const ingredients: IngredientState[] = recipe.ingredients.map((ing) => ({
      id: ing.id,
      rawProductId: ing.rawProductId,
      rawProduct: {
        id: ing.rawProduct.id,
        sku: ing.rawProduct.sku,
        name: ing.rawProduct.name,
        isActive: ing.rawProduct.isActive,
        recipeUnit: ing.rawProduct.recipeUnit,
      },
      quantity: ing.quantity,
      notes: ing.notes,
      sortOrder: ing.sortOrder,
      isNew: false,
      isModified: false,
      isDeleted: false,
      cost: ing.cost ?? null,
    }));

    setState({
      recipe,
      name: recipe.name,
      description: recipe.description,
      yield: recipe.yield,
      yieldUnitId: recipe.yieldUnitId,
      posProductIds: recipe.posProducts?.map(p => p.id) ?? [],
      ingredients,
      isDirty: false,
      errors: {},
    });
  }, []);

  // Update recipe info
  const updateRecipeInfo = useCallback((
    field: 'name' | 'description' | 'yield' | 'yieldUnitId',
    value: unknown
  ) => {
    setState((prev) => ({
      ...prev,
      [field]: value,
      isDirty: true,
      errors: { ...prev.errors, [field]: '' },
    }));
  }, []);

  // Set all POS product IDs
  const setPosProductIds = useCallback((ids: string[]) => {
    setState((prev) => ({
      ...prev,
      posProductIds: ids,
      isDirty: true,
    }));
  }, []);

  // Add a POS product
  const addPosProduct = useCallback((id: string) => {
    setState((prev) => {
      if (prev.posProductIds.includes(id)) return prev;
      return {
        ...prev,
        posProductIds: [...prev.posProductIds, id],
        isDirty: true,
      };
    });
  }, []);

  // Remove a POS product
  const removePosProduct = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      posProductIds: prev.posProductIds.filter(pid => pid !== id),
      isDirty: true,
    }));
  }, []);

  // Add an ingredient
  const addIngredient = useCallback((rawProduct: RawProductWithRelations) => {
    setState((prev) => {
      // Check if already exists
      const exists = prev.ingredients.some(
        (ing) => ing.rawProductId === rawProduct.id && !ing.isDeleted
      );
      if (exists) {
        return prev;
      }

      const maxSortOrder = prev.ingredients.reduce(
        (max, ing) => Math.max(max, ing.sortOrder),
        0
      );

      const newIngredient: IngredientState = {
        id: `temp_${Date.now()}`,
        rawProductId: rawProduct.id,
        rawProduct: {
          id: rawProduct.id,
          sku: rawProduct.sku,
          name: rawProduct.name,
          isActive: rawProduct.isActive ?? true,
          recipeUnit: rawProduct.recipeUnit,
          allergens: rawProduct.allergens,
        },
        quantity: 0,
        notes: null,
        sortOrder: maxSortOrder + 1,
        isNew: true,
        isModified: false,
        isDeleted: false,
        cost: null, // Will be calculated when quantity is set
      };

      return {
        ...prev,
        ingredients: [...prev.ingredients, newIngredient],
        isDirty: true,
      };
    });
  }, []);

  // Update an ingredient
  const updateIngredient = useCallback((
    id: string,
    field: 'quantity' | 'notes',
    value: unknown
  ) => {
    setState((prev) => ({
      ...prev,
      ingredients: prev.ingredients.map((ing) => {
        if (ing.id !== id) return ing;

        const updated = {
          ...ing,
          [field]: value,
          isModified: !ing.isNew,
        };

        // Note: Cost calculation would need PMP from backend
        // For now, we don't have PMP in the raw product data
        // The actual cost will be calculated server-side

        return updated;
      }),
      isDirty: true,
    }));
  }, []);

  // Remove an ingredient
  const removeIngredient = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      ingredients: prev.ingredients.map((ing) => {
        if (ing.id !== id) return ing;
        // If it's new, remove it entirely; otherwise mark as deleted
        if (ing.isNew) {
          return { ...ing, isDeleted: true }; // Filter out below
        }
        return { ...ing, isDeleted: true };
      }).filter((ing) => !(ing.isNew && ing.isDeleted)),
      isDirty: true,
    }));
  }, []);

  // Reorder ingredients
  const reorderIngredients = useCallback((ids: string[]) => {
    setState((prev) => ({
      ...prev,
      ingredients: ids.map((id, index) => {
        const ing = prev.ingredients.find((i) => i.id === id);
        if (!ing) return null;
        return {
          ...ing,
          sortOrder: index + 1,
          isModified: !ing.isNew,
        };
      }).filter(Boolean) as IngredientState[],
      isDirty: true,
    }));
  }, []);

  // Get update DTO for API
  const getUpdateDto = useCallback((): UpdateRecipeDto => {
    const ingredients: RecipeIngredientDto[] = state.ingredients
      .filter((ing) => !ing.isDeleted)
      .map((ing) => ({
        rawProductId: ing.rawProductId,
        quantity: ing.quantity,
        notes: ing.notes,
        sortOrder: ing.sortOrder,
      }));

    return {
      name: state.name,
      description: state.description,
      yield: state.yield,
      yieldUnitId: state.yieldUnitId,
      ingredients,
    };
  }, [state]);

  // Reset state
  const reset = useCallback(() => {
    if (state.recipe) {
      setRecipe(state.recipe);
    }
  }, [state.recipe, setRecipe]);

  // Check for errors
  const hasErrors = useCallback(() => {
    const errors: Record<string, string> = {};

    if (!state.name.trim()) {
      errors.name = 'Le nom est requis';
    }

    const activeIngredients = state.ingredients.filter((i) => !i.isDeleted);
    if (activeIngredients.length === 0) {
      errors.ingredients = 'Au moins un ingredient est requis';
    }

    for (const ing of activeIngredients) {
      if (ing.quantity <= 0) {
        errors[`ingredient_${ing.id}`] = 'La quantite doit etre positive';
      }
    }

    setState((prev) => ({ ...prev, errors }));
    return Object.keys(errors).length > 0;
  }, [state.name, state.ingredients]);

  // Calculate total cost locally
  const totalCost = useMemo(() => {
    const activeIngredients = state.ingredients.filter((i) => !i.isDeleted);
    const costs = activeIngredients.map((i) => i.cost);

    // If any cost is null, we can't calculate total
    if (costs.some((c) => c === null)) {
      return null;
    }

    return costs.reduce<number>((sum, c) => sum + (c ?? 0), 0);
  }, [state.ingredients]);

  // Get all allergens from ingredients
  const allergens = useMemo(() => {
    const set = new Set<string>();
    state.ingredients
      .filter((i) => !i.isDeleted)
      .forEach((ing) => {
        ing.rawProduct.allergens?.forEach((a) => set.add(a));
      });
    return Array.from(set).sort();
  }, [state.ingredients]);

  // Count active ingredients
  const activeIngredientsCount = useMemo(() => {
    return state.ingredients.filter((i) => !i.isDeleted).length;
  }, [state.ingredients]);

  // Count ingredients with missing cost
  const missingCostCount = useMemo(() => {
    return state.ingredients.filter((i) => !i.isDeleted && i.cost === null).length;
  }, [state.ingredients]);

  return {
    // State
    ...state,
    totalCost,
    allergens,
    activeIngredientsCount,
    missingCostCount,

    // Actions
    setRecipe,
    updateRecipeInfo,
    setPosProductIds,
    addPosProduct,
    removePosProduct,
    addIngredient,
    updateIngredient,
    removeIngredient,
    reorderIngredients,
    getUpdateDto,
    reset,
    hasErrors,
  };
}

export type RecipeBuilderHook = ReturnType<typeof useRecipeBuilder>;
